import {
  AddOrganizationRequest,
  AddOrganizationSeatRequest,
  OrganizationDetailResponse,
  OrganizationFilters,
  OrganizationInvitationListResponse,
  OrganizationInvitationsFilters,
  OrganizationListResponse,
  OrganizationSeatDetailResponse,
  OrganizationSeatFilters,
  OrganizationSeatListResponse,
  UpdateOrganizationPrimaryProviderRequest,
  UpdateOrganizationRequest,
} from "~/admins/types/organization";
import { TeamMemberWithOrganizationTeamMembers } from "~/common/utils";
import { api, PaginatedServiceResponse } from "./axios";

export const getOrganization = async (id: string) => {
  const { data } = await api.get<OrganizationDetailResponse>(
    `/v1/organizations/${id}`,
  );
  return data;
};

export const updateOrganization = async (
  payload: UpdateOrganizationRequest,
) => {
  const { data } = await api.put<OrganizationDetailResponse>(
    `/v1/organizations/${payload.id}`,
    payload,
  );
  return data;
};

export const updateOrganizationNote = async ({
  organizationId,
  internalNote,
}: {
  organizationId: string;
  internalNote: string;
}) => {
  const { data } = await api.patch<string>(
    `/v1/organizations/${organizationId}/note`,
    { internal_note: internalNote },
  );
  return data;
};

export const listOrganizations = async (filters?: OrganizationFilters) => {
  const { data } = await api.get<
    PaginatedServiceResponse<OrganizationListResponse[]>
  >("/v1/organizations", {
    params: filters,
    paramsSerializer: {
      indexes: null,
    },
  });
  return data;
};

export const listOrganizationSeats = async (
  organizationId: string,
  filters?: OrganizationSeatFilters,
) => {
  const { data } = await api.get<
    PaginatedServiceResponse<OrganizationSeatListResponse[]>
  >(`/v1/admins/organizations/${organizationId}/team-members`, {
    params: filters,
    paramsSerializer: {
      indexes: null,
    },
  });
  return data;
};

export const listOrganizationInvitations = async (
  organizationId: string,
  filters?: OrganizationInvitationsFilters,
) => {
  const { data } = await api.get<
    PaginatedServiceResponse<OrganizationInvitationListResponse[]>
  >(`/v1/admins/organizations/${organizationId}/team-members/invites`, {
    params: filters,
    paramsSerializer: {
      indexes: null,
    },
  });
  return data;
};

export const addOrganization = async (organization: AddOrganizationRequest) => {
  const { data } = await api.post<OrganizationDetailResponse>(
    "/v1/organizations",
    organization,
  );
  return data;
};

export const addOrganizationSeat = async (seat: AddOrganizationSeatRequest) => {
  const { data } = await api.post<OrganizationSeatDetailResponse>(
    "/v1/admins/team-members/invites",
    seat,
  );
  return data;
};

export const resendOrganizationInvitation = async ({
  invitationId,
}: {
  invitationId: string;
}) => {
  const { data } = await api.post<string>(
    `/v1/admins/team-members/invites/${invitationId}/resend`,
  );
  return data;
};

export const cancelOrganizationInvitation = async ({
  invitationId,
}: {
  invitationId: string;
}) => {
  const { data } = await api.patch<string>(
    `/v1/admins/team-members/invites/${invitationId}/cancel`,
  );
  return data;
};

export const deactivateOrganizationSeat = async ({
  seatId,
}: {
  seatId: string;
}) => {
  const { data } = await api.post<string>(
    `/v1/admins/organization-team-members/${seatId}/deactivate`,
  );
  return data;
};

export const listOrganizationProviders = async (organizationId: string) => {
  const { data } = await api.get<TeamMemberWithOrganizationTeamMembers[]>(
    `/v1/organizations/${organizationId}/providers`,
  );
  return data;
};

export const updateOrganizationPrimaryProvider = async ({
  organizationId,
  patientId,
  primaryProviderId,
}: UpdateOrganizationPrimaryProviderRequest) => {
  const res = await api.patch<string>(
    `/v1/team-members/organizations/${organizationId}/patients/${patientId}`,
    {
      primary_provider_id: primaryProviderId,
    },
  );
  return res.data;
};
